/* RESPONSIBLE TEAM: team-proactive-support */
import { tracked } from '@glimmer/tracking';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
import type Node from './node';
import type Graph from './graph';
import Coordinates, { type CoordinateLike } from './coordinates';

import StandardEdgeGenerator, { type EdgeGenerator } from './edge/generator';
import { type Alignment } from './connection-point';

interface Inputs<DataObjectType = any> {
  predecessor: Node;
  successor?: Node;
  dataObject?: DataObjectType;
}

interface UIProperties {
  path: string;
  arrowHead: string;
  startpoint: Coordinates;
  midpoint: Coordinates;
  endpoint: Coordinates;
  startAlignment: Alignment;
  endAlignment: Alignment;
}

export { Inputs };
export default class Edge<DataObjectType = any> {
  @tracked element?: HTMLElement;
  predecessor: Node;
  @tracked successor?: Node;
  @tracked generator: EdgeGenerator;
  @tracked dataObject?: DataObjectType;

  constructor(predecessor: Node, successor?: Node, dataObject?: DataObjectType) {
    this.predecessor = predecessor;
    this.successor = successor;
    this.dataObject = dataObject;
    this.generator = new StandardEdgeGenerator(this);
  }

  get graph(): Graph {
    return this.predecessor.graph;
  }

  @action setElement(element: HTMLElement): void {
    this.element = element;
  }

  get isDragging(): boolean {
    return this.graph.state.draggingEdge === this;
  }

  get isSelected(): boolean {
    return this.graph.state.selectedEdge === this;
  }

  get isMouseAtEdge(): boolean {
    return this.graph.state.mouseAtEdge === this;
  }

  @computed('graph.state.hoveredEdges.[]')
  get isHovered(): boolean {
    return this.graph.state.hoveredEdges.includes(this);
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'generator.path.segments.length',
    'generator.startAndEndCoordinatesPairs.[]',
    'generator.{end,from,midpoint,start,to}',
    'graph.nodes',
    'graph.state.nodesHaveBeenRendered',
    'dataObject.isDeleted',
  )
  get uiProperties(): UIProperties {
    if (!this.graph.state.nodesHaveBeenRendered) {
      return Edge.defaultUIProperties;
    }

    // It is safe to force this as any as undefined is falsy
    if ((<any>this.dataObject)?.isDeleted) {
      return Edge.defaultUIProperties;
    }

    let foundNonOverlappingPath = false;
    let coordinatePairs = this.generator.startAndEndCoordinatesPairs;

    for (let i = 0; i < coordinatePairs.length; ++i) {
      let coordinatePair = this.generator.startAndEndCoordinatesPairs[i];
      this.updateGeneratorFromCoordinatePair(coordinatePair);

      // If we're dealing with dragging an edge, i.e. an edge not connected to a node,
      // just return immediately rather than checking for overlaps
      if (this.generator.path.segments.length === 1 && this.generator.to === 'no-alignment') {
        foundNonOverlappingPath = true;
        break;
      }

      // A path containing a diagonal line should be skipped. These represent poor choice fallbacks where we cannot
      // draw an S or L shaped line.
      if (this.generator.path.segments.some((segment) => segment.direction === 'diagonal')) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (!this.generator.path.overlaps(this.graph.nodes.toArray())) {
        foundNonOverlappingPath = true;
        break;
      }
    }

    // If no paths found, default to the closest coordinate pair
    if (!foundNonOverlappingPath) {
      this.updateGeneratorFromCoordinatePair(coordinatePairs[0]);
    }

    let path = this.generator.path;

    return {
      path: path.svg,
      arrowHead: path.arrowHead,
      startpoint: this.generator.start,
      midpoint: this.generator.midpoint,
      endpoint: this.generator.end,
      startAlignment: this.generator.from ?? 'no-alignment',
      endAlignment: this.generator.to ?? 'no-alignment',
    };
  }

  private updateGeneratorFromCoordinatePair(
    coordinatePair: [CoordinateLike, CoordinateLike],
  ): void {
    let start: CoordinateLike;
    let end: CoordinateLike;
    let from: Alignment;
    let to: Alignment;

    start = coordinatePair[0];
    end = coordinatePair[1];

    from = start.alignment;
    to = end.alignment;

    this.generator.update({
      start: start.asCoordinates(),
      end: end.asCoordinates(),
      from,
      to,
      spacing: this.predecessor.graph.settings.grid.spacing / 2,
    });
  }

  @computed()
  private static get defaultUIProperties(): UIProperties {
    return {
      path: '',
      arrowHead: '',
      startpoint: new Coordinates(0, 0),
      endpoint: new Coordinates(0, 0),
      midpoint: new Coordinates(0, 0),
      startAlignment: 'no-alignment',
      endAlignment: 'no-alignment',
    };
  }
}
